import { useEffect, useId, useRef, useState } from "react";
import { Icon } from "./Icon";

export function SidebarAccountMenu({ onLogout }: { onLogout(): Promise<void> }) {
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const logoutRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    logoutRef.current?.focus();
    const outside = (event: Event) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target))
        setOpen(false);
    };
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        event.preventDefault();
        logoutRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    document.addEventListener("keydown", keyboard);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", outside);
      document.removeEventListener("keydown", keyboard);
    };
  }, [open]);

  return (
    <div className="sidebar-account-menu" ref={containerRef}>
      <button
        ref={triggerRef}
        className="account-menu-trigger"
        type="button"
        aria-label="Account options"
        title="Account options"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={busy}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <Icon name="more" />
      </button>
      {open ? (
        <div className="account-popover" id={menuId}>
          <div role="menu" aria-label="Account actions">
            <button
              ref={logoutRef}
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() => {
                if (busy) return;
                setBusy(true);
                setError("");
                void onLogout()
                  .then(() => {
                    setOpen(false);
                    triggerRef.current?.focus();
                  })
                  .catch((cause) => {
                    setError(
                      cause instanceof Error
                        ? cause.message
                        : "Log out could not be completed. Try again.",
                    );
                    setOpen(true);
                  })
                  .finally(() => setBusy(false));
              }}
            >
              <Icon name="arrow" />
              <span>
                <strong>{busy ? "Logging out…" : "Log out"}</strong>
                <small>End this session on this device</small>
              </span>
            </button>
          </div>
          {error ? (
            <p className="account-menu-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
